import { combinedStudies } from './combinedStudies.js';
import { createLiveStudies } from './liveStudies.js';
import { readWatcherStream } from './watcherStream.js';

const base = 'http://127.0.0.1:43127';
export async function connectReportWatcher(
  directory,
  token,
  studyDirectory,
  studies = [],
  onStudiesUpdate = () => {},
  secondaryStudyDirectory = null
) {
  const headers = { 'X-HR-Watcher-Token': token.trim() };
  const response = await fetch(base + '/info', { headers, cache: 'no-store' });
  const info = await response.json();
  if (!response.ok) throw new Error(info.error || 'Local watcher failed.');
  if (info.pushVersion !== 1)
    throw new Error('Restart the updated local helper before enabling Auto Create.');
  try {
    const handle = await directory.getFileHandle(info.marker);
    if ((await (await handle.getFile()).text()) !== info.session) throw new Error('Wrong marker');
  } catch {
    throw new Error(`Select the same PDF folder as the helper: ${info.path}`);
  }
  if (info.studyVersion !== 2 || !studyDirectory)
    throw new Error('Restart the updated helper with both PDF and study folders selected.');
  try {
    const handle = await studyDirectory.getFileHandle(info.studyMarker);
    if ((await (await handle.getFile()).text()) !== info.session) throw new Error('Wrong marker');
  } catch {
    throw new Error(`Select the same study folder as the helper: ${info.studyPath}`);
  }
  if (Boolean(info.secondaryStudyPath) !== Boolean(secondaryStudyDirectory))
    throw new Error('Select the same optional secondary study folder in the helper and webpage.');
  if (secondaryStudyDirectory) {
    try {
      const handle = await secondaryStudyDirectory.getFileHandle(info.secondaryStudyMarker);
      if ((await (await handle.getFile()).text()) !== info.session) throw new Error('Wrong marker');
    } catch {
      throw new Error(`Select the helper secondary study folder: ${info.secondaryStudyPath}`);
    }
  }
  // Startup inventories repopulate both roots. Keep raw IDs inside each cache.
  const live = createLiveStudies(studyDirectory, []);
  const secondaryLive = secondaryStudyDirectory
    ? createLiveStudies(secondaryStudyDirectory, [])
    : null;
  let currentStudies = studies;

  const inventoryResponse = await fetch(base + '/studies', { headers, cache: 'no-store' });
  if (!inventoryResponse.ok) throw new Error('Unable to load helper study inventory.');
  const inventory = await inventoryResponse.json();
  const studyChanges = new Set(inventory.names);
  const secondaryChanges = new Set(inventory.secondaryNames || []);
  let cursor = info.cursor;
  let initial = true;
  let initialStudies = true;
  let connected = false;
  let fatal = '';
  let wake = () => {};
  const changed = new Set();
  const pending = new Map();
  const checkedAt = new Map();
  const pause = (ms, signal) =>
    new Promise((resolve) => {
      let timer;
      const finish = () => {
        clearTimeout(timer);
        signal.removeEventListener('abort', finish);
        if (wake === finish) wake = () => {};
        resolve();
      };
      wake = finish;
      signal.addEventListener('abort', finish, { once: true });
      if (Number.isFinite(ms)) timer = setTimeout(finish, ms);
      if (signal.aborted) finish();
    });
  const start = async (signal, onStatus) => {
    let retry = 1000;
    while (!signal.aborted && !fatal) {
      const connection = new AbortController();
      const abort = () => connection.abort();
      signal.addEventListener('abort', abort, { once: true });
      let timeout;
      const heartbeat = () => {
        clearTimeout(timeout);
        timeout = setTimeout(abort, 45000);
      };
      try {
        onStatus('Connecting to local helper…');
        heartbeat();
        const stream = await fetch(
          `${base}/events?session=${encodeURIComponent(info.session)}&after=${cursor}`,
          { headers, signal: connection.signal, cache: 'no-store' }
        );
        if (!stream.ok) {
          const error = await stream.json();
          if ([400, 401, 403, 409, 503].includes(stream.status))
            fatal = error.error || 'Helper requires restart.';
          throw new Error(error.error || 'Helper connection failed.');
        }
        connected = true;
        retry = 1000;
        onStatus('Connected — waiting for pushed file notifications.');
        wake();
        await readWatcherStream(
          stream.body,
          (event, data) => {
            if (event === 'reset') {
              fatal = data.error || 'Restart Auto Create to resynchronize.';
              throw new Error(fatal);
            }
            if (event !== 'changes') return;
            if (!Number.isSafeInteger(data.cursor) || !Array.isArray(data.names))
              throw new Error('Invalid helper event.');
            if (data.cursor < cursor) return;
            for (const name of data.names) {
              if (
                typeof name === 'string' &&
                /^\d+(?:[a-z][a-z0-9]*)?\.pdf$/i.test(name) &&
                !/_hr/i.test(name)
              )
                changed.add(name);
            }
            for (const name of data.studyNames || []) {
              if (typeof name === 'string') studyChanges.add(name);
            }
            for (const name of data.secondaryStudyNames || []) {
              if (typeof name === 'string') secondaryChanges.add(name);
            }
            // Acknowledge only after names are retained in the in-memory work queue.
            cursor = data.cursor;
            wake();
          },
          heartbeat
        );
      } catch (error) {
        if (!signal.aborted)
          onStatus(fatal ? `Stopped: ${fatal}` : `Disconnected — reconnecting. ${error.message}`);
      } finally {
        clearTimeout(timeout);
        connection.abort();
        signal.removeEventListener('abort', abort);
        connected = false;
        wake();
      }
      if (signal.aborted || fatal) break;
      // Reconnect backoff is not a file/folder poll. Separate from the work waiter.
      await new Promise((resolve) => {
        const finish = () => {
          clearTimeout(timer);
          signal.removeEventListener('abort', finish);
          resolve();
        };
        const timer = setTimeout(finish, retry);
        signal.addEventListener('abort', finish, { once: true });
        if (signal.aborted) finish();
      });
      retry = Math.min(retry * 2, 15000);
    }
    wake();
  };
  const ready = async (signal) => {
    for (;;) {
      if (connected || fatal || signal.aborted) break;
      await pause(Infinity, signal);
    }
    if (fatal) throw new Error(fatal);
    if (signal.aborted) throw new Error('Auto Create stopped.');
  };
  const prune = (seen) => {
    for (const name of pending.keys()) {
      const state = seen.get(name);
      if (
        state?.failed ||
        state?.excluded ||
        state?.completed ||
        /_hr/i.test(name) ||
        !/^\d+(?:[a-z][a-z0-9]*)?\.pdf$/i.test(name)
      )
        pending.delete(name);
    }
  };
  const collectReports = async (seen, signal) => {
    await ready(signal);
    if (initialStudies || studyChanges.size || secondaryChanges.size) {
      const paths = [...studyChanges];
      const secondaryPaths = [...secondaryChanges];
      paths.forEach((path) => studyChanges.delete(path));
      secondaryPaths.forEach((path) => secondaryChanges.delete(path));
      const updatedIds = await live.update(paths, signal, initialStudies);
      if (secondaryLive) {
        for (const id of await secondaryLive.update(secondaryPaths, signal, initialStudies))
          updatedIds.add(id);
      }
      initialStudies = false;
      currentStudies = combinedStudies(live.getStudies(), secondaryLive?.getStudies() || []);
      if (!signal.aborted)
        onStudiesUpdate(currentStudies, {
          primary: live.getStudies(),
          secondary: secondaryLive?.getStudies() || [],
        });
      for (const [name, state] of seen) {
        if (state.unmatched && updatedIds.has(state.studyId)) {
          seen.delete(name);
          changed.add(name);
        }
      }
    }
    if (initial) {
      // Only startup enumerates the folder. Events during this scan stay queued.
      const reports = [];
      for await (const [name, handle] of directory.entries()) {
        if (signal.aborted) throw new Error('Auto Create stopped.');
        if (handle.kind === 'file' && /\.pdf$/i.test(name)) reports.push({ name, handle });
      }
      reports.sort((a, b) => a.name.localeCompare(b.name));
      const existingNames = new Set(reports.map((report) => report.name.toLowerCase()));
      reports.forEach((report) => {
        if (
          !/^\d+(?:[a-z][a-z0-9]*)?\.pdf$/i.test(report.name) ||
          /_hr/i.test(report.name) ||
          existingNames.has(report.name.replace(/\.pdf$/i, '_HR.pdf').toLowerCase())
        )
          return;
        pending.set(report.name, report);
        checkedAt.set(report.name, Date.now());
      });
      initial = false;
      return reports;
    }
    prune(seen);
    const names = [...changed];
    names.forEach((name) => changed.delete(name));
    for (const name of names) {
      try {
        const handle = await directory.getFileHandle(name);
        if (seen.get(name)?.excluded) seen.delete(name);
        pending.set(name, { name, handle, arrived: true });
      } catch (error) {
        if (error.name !== 'NotFoundError') {
          names.forEach((value) => changed.add(value));
          throw error;
        }
        pending.delete(name);
        seen.delete(name);
      }
    }
    const reports = [...pending.values()].filter(
      (report) => Date.now() - (checkedAt.get(report.name) ?? -Infinity) >= 2000
    );
    reports.forEach((report) => checkedAt.set(report.name, Date.now()));
    return reports;
  };
  const waitForWork = async (seen, signal) => {
    while (!signal.aborted) {
      await ready(signal);
      prune(seen);
      if (changed.size || studyChanges.size || secondaryChanges.size) return;
      if (pending.size) {
        const due = Math.min(
          ...[...pending.keys()].map((name) => (checkedAt.get(name) ?? 0) + 2000)
        );
        await pause(Math.max(0, due - Date.now()), signal);
        if (connected) return;
      } else await pause(Infinity, signal);
    }
  };
  return {
    collectReports,
    waitForWork,
    start,
    path: info.path,
    studyPath: info.studyPath,
    secondaryStudyPath: info.secondaryStudyPath,
    getStudies: () => currentStudies,
  };
}
